import { useCallback, useRef } from 'react';
import { useWindowEvent } from '@zocdoc-frontend-common/utils-react';
import useHideMain from './useHideMain';
import useFocusTrap from './useFocusTrap';
import useAutofocus from './useAutofocus';
import handlePropValidation from '../utils/handlePropValidation';

/**
 * This hook should be run on mount of a modal. It will:
 * create refs for the modal, the close icon and the title.
 * setup the accessibility hooks and event listeners.
 * run the prop validation for the modal.
 * This will return the refs and callbacks needed by the child modal components.
 * @param {string} title - Title of your modal which will be automatically styled.
 * @param {boolean} ignoreClickOutside - if true, clicking outside of the modal content will not close the modal.
 * @param {function} onClose - Callback which is triggered to close the modal. If this is undefined, no close icon will be rendered.
 * @param {boolean} whiteOverlay - indicates whether to use white overlay. This will be deprecated soon.
 * This is only used for vaccine related features and will eventually be torn down.
 * @param {Modal.Button} primaryButtonProps - Props for the primary button. If this is null\undefined, the onClose prop must be set.
 * @param {Modal.Button} secondaryButtonProps - Props for the secondary button. If this is set, the primaryButton prop must also be set.
 * @param {React.Ref} [overrideAutofocusRef=null] - ref attached to an element in the modal content which will get auto-focus if it is fully visible.
 * This will take precedence over the close icon and the title.
 * @returns Object containing the useRefs and 2 callback functions. All need to be passed down to child components.
 */
export default function useModal({
    title,
    ignoreClickOutside,
    onClose,
    whiteOverlay,
    primaryButtonProps,
    secondaryButtonProps,
    overrideAutofocusRef,
}) {
    const contentRef = useRef();
    const closeRef = useRef();
    const titleRef = useRef();

    useFocusTrap(contentRef);
    useAutofocus({
        closeRef,
        contentRef,
        overrideAutofocusRef,
        titleRef,
    });
    useHideMain(contentRef);

    // hitting Esc should close modal
    useWindowEvent(
        'keydown',
        useCallback(
            e => {
                if (
                    typeof onClose === 'function' &&
                    // I.E. uses 'Esc'
                    (e.key === 'Escape' || e.key === 'Esc')
                ) {
                    e.preventDefault();
                    onClose();
                }
            },
            [onClose]
        )
    );

    handlePropValidation({
        title,
        whiteOverlay,
        onClose,
        primaryButtonProps,
        secondaryButtonProps,
    });

    // hitting spacebar when focused on close icon should close modal
    const handleKeyDownCloseIcon = useCallback(
        e => {
            if (e.key === ' ') {
                e.preventDefault();
                onClose();
            }
        },
        [onClose]
    );

    const handleClickOverlay = useCallback(
        e => {
            if (
                !ignoreClickOutside &&
                onClose &&
                e.target === e.currentTarget
            ) {
                onClose();
            }
        },
        [ignoreClickOutside, onClose]
    );

    return {
        contentRef,
        closeRef,
        titleRef,
        handleKeyDownCloseIcon,
        handleClickOverlay,
    };
}
